import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export class PdfPrintService {
  /**
   * Abre uma nova aba com o conteúdo formatado para impressão
   */
  openPrintWindow(preCadastro: any, attachments: any[] = []): void {
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      alert('Por favor, permita pop-ups para gerar o PDF');
      return;
    }

    const fileName = this.generateFileName(preCadastro);
    const htmlContent = this.generatePrintHTML(preCadastro, attachments, fileName);

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Aguardar o carregamento e executar impressão automaticamente
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Opcional: fechar a aba após impressão
        // printWindow.onafterprint = () => printWindow.close();
      }, 500);
    };
  }

  /**
   * Gera nome padrão para o arquivo de impressão
   */
  private generateFileName(preCadastro: any): string {
    const currentDate = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    const name = preCadastro.fullName || 'PreCadastro';
    const id = preCadastro.id || 'SemID';

    // Remove caracteres especiais e espaços para um nome de arquivo válido
    const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');

    return `PreCadastro_${cleanName}_ID${id}_${currentDate}`;
  }

  /**
   * Gera o HTML completo para impressão
   */
  private generatePrintHTML(
    preCadastro: any,
    attachments: any[],
    fileName?: string
  ): string {
    const documentTitle =
      fileName || `Dados Cadastrais - ${preCadastro.fullName || preCadastro.id}`;

    return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${documentTitle}</title>
    <style>
        ${this.getPrintStyles()}
    </style>
</head>
<body>
    <div class="print-container">
        ${this.generateHeader(preCadastro)}
        ${this.generateMainData(preCadastro)}
        ${this.generateAttachmentsSection(attachments)}
        ${this.generateFooter()}
    </div>
</body>
</html>`;
  }

  /**
   * Estilos CSS otimizados para impressão (EXATAMENTE IGUAL AO ANGULAR)
   */
  private getPrintStyles(): string {
    return `
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #333;
            background: white;
        }

        .print-container {
            max-width: 210mm;
            margin: 0 auto;
            padding: 15mm;
        }

        /* Cabeçalho */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20pt;
            padding-bottom: 10pt;
            border-bottom: 2pt solid #2196F3;
        }

        .company-info h1 {
            font-size: 18pt;
            font-weight: bold;
            color: #2196F3;
            margin-bottom: 4pt;
        }

        .company-info p {
            font-size: 12pt;
            color: #666;
        }

        .qr-code-section {
            text-align: right;
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .qr-code-section img {
            max-width: 150px;
            max-height: 150px;
            border: 1pt solid #ddd;
            border-radius: 4pt;
        }

        .document-info {
            text-align: right;
            font-size: 9pt;
            color: #666;
        }

        /* Seções */
        .section {
            margin-bottom: 20pt;
            page-break-inside: avoid;
        }

        .section-title {
            font-size: 14pt;
            font-weight: bold;
            color: #2196F3;
            margin-bottom: 12pt;
            padding-bottom: 4pt;
            border-bottom: 1pt solid #e0e0e0;
            text-transform: uppercase;
        }

        /* Grid de dados */
        .data-grid {
            display: table;
            width: 100%;
            border-collapse: collapse;
        }

        .data-row {
            display: table-row;
            margin-bottom: 8pt;
        }

        .data-item {
            display: table-cell;
            vertical-align: top;
            padding: 6pt 8pt;
            border: 1pt solid #e0e0e0;
            background: #f8f9fa;
        }

        .data-item.full-width {
            width: 100%;
        }

        .data-label {
            font-weight: 600;
            font-size: 9pt;
            color: #555;
            text-transform: uppercase;
            margin-bottom: 3pt;
            display: block;
        }

        .data-value {
            font-size: 10pt;
            color: #333;
            word-wrap: break-word;
        }

        /* Status badges */
        .status-badge {
            padding: 3pt 6pt;
            border-radius: 8pt;
            font-size: 8pt;
            font-weight: 600;
            text-transform: uppercase;
            color: white;
            display: inline-block;
        }

        .status-pendente { background: #ff9800; }
        .status-analisando { background: #ff5722; }
        .status-verificando-contrato { background: #ff5722; }
        .status-aprovado { background: #2196F3; }
        .status-aguardando-assinaturas { background: #2196F3; }
        .status-concluido { background: #4caf50; }
        .status-reprovado { background: #f44336; }
        .status-deletado { background: #f44336; }

        /* Separadores */
        .separator {
            border: none;
            border-top: 1pt solid #e0e0e0;
            margin: 15pt 0;
        }

        /* Tabela de anexos */
        .attachments-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
            margin-top: 10pt;
        }

        .attachments-table th,
        .attachments-table td {
            padding: 4pt 6pt;
            text-align: left;
            border: 1pt solid #ddd;
            vertical-align: top;
        }

        .attachments-table th {
            background: #f0f0f0;
            font-weight: 600;
            color: #333;
            font-size: 8pt;
            text-transform: uppercase;
        }

        .attachments-table tbody tr:nth-child(even) {
            background: #f8f9fa;
        }

        .type-badge {
            padding: 2pt 4pt;
            border-radius: 6pt;
            font-size: 7pt;
            font-weight: 600;
            color: white;
            display: inline-block;
        }

        .type-contrato { background: #1976d2; }
        .type-recibo_kit { background: #7b1fa2; }
        .type-outros { background: #388e3c; }

        .situation-badge {
            padding: 2pt 4pt;
            border-radius: 6pt;
            font-size: 7pt;
            font-weight: 600;
            display: inline-block;
        }

        .situation-aguardando-assinaturas { background: #fff3e0; color: #e65100; }
        .situation-verificando-assinatura { background: #fff8e1; color: #f57f17; }
        .situation-assinado { background: #e8f5e8; color: #2e7d32; }
        .situation-aguardando-pagamento { background: #e3f2fd; color: #1565c0; }
        .situation-pago { background: #e8f5e8; color: #2e7d32; }

        /* Rodapé */
        .footer {
            margin-top: 20pt;
            padding-top: 10pt;
            border-top: 1pt solid #e0e0e0;
            text-align: center;
            font-size: 8pt;
            color: #666;
        }

        /* Quebras de página */
        .page-break {
            page-break-before: always;
        }

        /* Estilos para impressão */
        @media print {
            body {
                font-size: 10pt;
            }
            
            .print-container {
                margin: 0;
                padding: 10mm;
            }
            
            .section {
                page-break-inside: avoid;
            }
        }

        @page {
            margin: 15mm;
            size: A4;
        }
    `;
  }

  /**
   * Gera o cabeçalho do documento (EXATAMENTE IGUAL AO ANGULAR)
   */
  private generateHeader(preCadastro: any): string {
    const qrCodeData = this.generateQRCodeData(preCadastro);

    return `
        <div class="header">
            <div class="company-info">
                <h1>X DO INFINITO</h1>
                <p>Dados Cadastrais - Pré-Cadastro</p>
                <div><strong>ID:</strong> ${preCadastro.id || 'N/A'} | Data: ${new Date(preCadastro.createdAt).toLocaleDateString() || 'N/A'} <br/>
                <strong>Indicador:</strong> ${preCadastro.indicatedCode || 'N/A'} ${preCadastro.indicador_nome || ''} (${preCadastro.indicador_role || ''})</div>
            </div>
            <div class="qr-code-section">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrCodeData)}" 
                     alt="QR Code com dados de contato" 
                     style="width: 100px; height: 100px; border: 1pt solid #ddd;" />
            </div>
        </div>
    `;
  }

  /**
   * Gera os dados para o QR Code (EXATAMENTE IGUAL AO ANGULAR)
   */
  private generateQRCodeData(preCadastro: any): string {
    return `Nome: ${preCadastro.fullName || 'N/A'}
Telefone: ${this.formatPhone(preCadastro.phone)}
1ª Referência
${preCadastro.referenceNameOne || 'N/A'} - ${this.formatPhone(preCadastro.referencePhoneOne)}
2ª Referência
${preCadastro.referenceNameTwo || 'N/A'} - ${this.formatPhone(preCadastro.referencePhoneTwo)}
Referência Comercial
${preCadastro.referenceComercial || 'N/A'} - ${this.formatPhone(preCadastro.referenceComercialPhone)}`;
  }

  /**
   * Gera a seção de dados principais (EXATAMENTE IGUAL AO ANGULAR)
   */
  private generateMainData(preCadastro: any): string {
    return `
        <div class="section">
            <div class="data-grid">
                <!-- Primeira linha -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Nome Completo</span>
                        <span class="data-value">${preCadastro.fullName || '-'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Documentos</span>
                        <span class="data-value">
                            RG: <strong>${this.formatRg(preCadastro.rgDocument)}</strong><br>
                            CPF: <strong>${this.formatCpf(preCadastro.cpfDocument)}</strong>
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Status</span>
                        <span class="data-value">
                            ${this.getStatusBadge(preCadastro.situacao)}
                        </span>
                    </div>
                </div>

                <!-- Segunda linha -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Contatos & Sociais</span>
                        <span class="data-value">
                            Telefone: <strong>${this.formatPhone(preCadastro.phone)}</strong><br>
                            E-mail: <strong>${preCadastro.email || '-'}</strong><br>
                            Instagram: <strong>${preCadastro.instagram || '-'}</strong>
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Dados Cívicos</span>
                        <span class="data-value">
                            Escolaridade: <strong>${preCadastro.scholarLevel || '-'}</strong><br>
                            Nasc.: <strong>${this.formatDate(preCadastro.dateOfBirth)}</strong><br>
                            Estado Civil: <strong>${preCadastro.maritalStatus || '-'}</strong><br>
                            Nacionalidade: <strong>${preCadastro.nationality || '-'}</strong>
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Localidade</span>
                        <span class="data-value">
                            Cidade/Estado: <strong>${preCadastro.cityState || '-'}</strong><br>
                            Bairro: <strong>${preCadastro.neighborhood || '-'}</strong><br>
                            Endereço: <strong>${preCadastro.address || '-'}</strong><br>
                            CEP: <strong>${this.formatCep(preCadastro.cep)}</strong>
                        </span>
                    </div>
                </div>

                <!-- Terceira linha -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Restrições & Crédito</span>
                        <span class="data-value">
                            Restrição: <strong>${preCadastro.cpfRestrictions || '-'}</strong><br>
                            Avalista: <strong>${preCadastro.avalist || '-'}</strong><br>
                            Cartão de Crédito: <strong>${preCadastro.creditCard || '-'}</strong>
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Parentalidade</span>
                        <span class="data-value">
                            Nome da Mãe: <strong>${preCadastro.mothersName || '-'}</strong><br>
                            Filhos: <strong>${preCadastro.children || '-'}</strong>
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Residência & Profissão</span>
                        <span class="data-value">
                            <small>
                                Moradia: <strong>${preCadastro.residence || '-'}</strong><br>
                                Período: <strong>${preCadastro.residenceTime || '-'}</strong><br>
                                Profissão: <strong>${preCadastro.job || '-'}</strong><br>
                                Renda Mensal: <strong>${preCadastro.income || '-'}</strong>
                            </small>
                        </span>
                    </div>
                </div>

                <!-- Quarta linha -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Motivação p/vender</span>
                        <span class="data-value">${preCadastro.salesMotivation || '-'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Objetivo de Lucro/mês</span>
                        <span class="data-value">${preCadastro.profitObjective || '-'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Dúvidas</span>
                        <span class="data-value">${preCadastro.doubt || '-'}</span>
                    </div>
                </div>

                <!-- Quinta linha - Referências -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">1ª Referência</span>
                        <span class="data-value">
                            ${preCadastro.referenceNameOne || '-'}<br>
                            ${this.formatPhone(preCadastro.referencePhoneOne)}
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">2ª Referência</span>
                        <span class="data-value">
                            ${preCadastro.referenceNameTwo || '-'}<br>
                            ${this.formatPhone(preCadastro.referencePhoneTwo)}
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Referência Comercial</span>
                        <span class="data-value">
                            ${preCadastro.referenceComercial || '-'}<br>
                            ${this.formatPhone(preCadastro.referenceComercialPhone)}
                        </span>
                    </div>
                </div>

                <!-- Sexta linha - Indicador e Interesse -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Indicador</span>
                        <span class="data-value">
                            ${preCadastro.indicatedCode
                              ? `#${preCadastro.indicatedCode} ${preCadastro.indicador_nome || ''} (${preCadastro.indicador_role || ''})`
                              : '---'}
                        </span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Interesse utilizar Xizinha</span>
                        <span class="data-value">${preCadastro.grauInteresse || '---'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Aprendizado utilização do aplicativo</span>
                        <span class="data-value">${preCadastro.aprenderAplicativo || '---'}</span>
                    </div>
                </div>

                <!-- Sétima linha - Tecnologia, Contrato e Embaixadora -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Uso de tecnologia/redes sociais</span>
                        <span class="data-value">${preCadastro.usoTecnologia || '---'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Acordo Contrato</span>
                        <span class="data-value">${preCadastro.acordoContrato || '---'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Motivo Embaixador</span>
                        <span class="data-value">${preCadastro.ambassadorReason || '---'}</span>
                    </div>
                </div>

                <!-- Oitava linha - Veículo, Entrevista e Treinamento -->
                <div class="data-row">
                    <div class="data-item">
                        <span class="data-label">Veículo</span>
                        <span class="data-value">${preCadastro.veiculo || '---'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Disponível para Entrevista Online</span>
                        <span class="data-value">${preCadastro.disponivelEntrevistaOnline || '---'}</span>
                    </div>
                    <div class="data-item">
                        <span class="data-label">Disponível para Treinamento de Vendas</span>
                        <span class="data-value">${preCadastro.disponivelTreinamentoVendas || '---'}</span>
                    </div>
                </div>
            </div>
        </div>
    `;
  }

  /**
   * Gera a seção de anexos (EXATAMENTE IGUAL AO ANGULAR)
   */
  private generateAttachmentsSection(attachments: any[]): string {
    if (!attachments || attachments.length === 0) {
      return `
        <div class="section">
            <h2 class="section-title">Anexos</h2>
            <p style="color: #666; font-style: italic;">Nenhum anexo encontrado.</p>
        </div>
      `;
    }

    const tableRows = attachments
      .map(
        (attachment) => `
        <tr>
            <td>${attachment.id || '-'}</td>
            <td>
                <strong>${attachment.name || '-'}</strong>
                ${attachment.description ? `<br><small style="color: #666;">${attachment.description}</small>` : ''}
            </td>
            <td>
                <span class="type-badge type-${attachment.attachmentType || 'outros'}">
                    ${this.getTypeText(attachment.attachmentType)}
                </span>
            </td>
            <td>
                <span class="situation-badge situation-${(attachment.situacao || '').replace('_', '-')}">
                    ${this.getSituationText(attachment.situacao)}
                </span>
            </td>           
        </tr>
    `
      )
      .join('');

    return `
        <div class="section">
            <h2 class="section-title">Anexos</h2>
            <table class="attachments-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Nome</th>
                        <th>Tipo</th>
                        <th>Situação</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRows}
                </tbody>
            </table>
        </div>
    `;
  }

  /**
   * Gera o rodapé do documento (EXATAMENTE IGUAL AO ANGULAR)
   */
  private generateFooter(): string {
    const currentDateTime = new Date().toLocaleString('pt-BR');

    return `
        <div class="footer">
            <p>Documento gerado automaticamente pelo sistema XDO INFINITO</p>
            <p>Data de geração: ${currentDateTime}</p>
        </div>
    `;
  }

  /**
   * Gera badge de status (EXATAMENTE IGUAL AO ANGULAR)
   */
  private getStatusBadge(situacao: string): string {
    const statusMap: { [key: string]: string } = {
      pendente: 'PENDENTE',
      analisando: 'ANÁLISE EM ANDAMENTO',
      verificando_contrato: 'VERIFICANDO CONTRATO',
      aprovado: 'APROVADO',
      aguardando_assinaturas: 'AGUARDANDO ASSINATURA & KIT',
      concluido: 'CONCLUÍDO',
      reprovado: 'REPROVADO',
      deletado: 'REMOVIDO',
    };

    const text = statusMap[situacao] || situacao || 'N/A';
    const className = `status-${(situacao || '').replace('_', '-')}`;

    return `<span class="status-badge ${className}">${text}</span>`;
  }

  /**
   * Formata data (EXATAMENTE IGUAL AO ANGULAR)
   */
  private formatDate(dateString: string): string {
    if (!dateString) return '-';

    if (dateString.length === 8) {
      return `${dateString.substring(0, 2)}/${dateString.substring(2, 4)}/${dateString.substring(4, 8)}`;
    }

    return dateString;
  }

  /**
   * Obtém texto do tipo de anexo (EXATAMENTE IGUAL AO ANGULAR)
   */
  private getTypeText(type: string): string {
    const typeMap: { [key: string]: string } = {
      contrato: 'Contrato Online',
      recibo_kit: 'Kit Maleta',
      outros: 'Outros',
    };
    return typeMap[type] || type || 'N/A';
  }

  /**
   * Obtém texto da situação (EXATAMENTE IGUAL AO ANGULAR)
   */
  private getSituationText(situacao: string): string {
    const situationMap: { [key: string]: string } = {
      aguardando_assinaturas: 'Aguardando Assinaturas',
      verificando_assinatura: 'Verificando Assinatura',
      assinado: 'Assinado',
      aguardando_pagamento: 'Aguardando Pagamento',
      pago: 'Pago',
    };
    return situationMap[situacao] || situacao || 'N/A';
  }

  /**
   * Formata CPF no padrão 000.000.000-00 (EXATAMENTE IGUAL AO ANGULAR)
   */
  private formatCpf(cpf: string): string {
    if (!cpf) return '-';

    // Remove caracteres não numéricos
    const cleanCpf = cpf.replace(/\D/g, '');

    // Verifica se tem 11 dígitos
    if (cleanCpf.length !== 11) return cpf;

    // Aplica a máscara
    return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  /**
   * Formata RG removendo caracteres especiais desnecessários (EXATAMENTE IGUAL AO ANGULAR)
   */
  private formatRg(rg: string): string {
    if (!rg) return '-';

    // Remove caracteres não alfanuméricos exceto pontos e hífens
    const cleanRg = rg.replace(/[^\w.-]/g, '');

    // Se for só números, aplica formatação padrão (assumindo RG de SP)
    if (/^\d+$/.test(cleanRg) && cleanRg.length >= 8) {
      return cleanRg.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4');
    }

    return cleanRg || rg;
  }

  /**
   * Formata telefone nos padrões (00) 0000-0000 ou (00) 00000-0000 (EXATAMENTE IGUAL AO ANGULAR)
   */
  private formatPhone(phone: string): string {
    if (!phone) return '-';

    // Remove caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');

    // Telefone fixo: (00) 0000-0000
    if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    // Celular: (00) 00000-0000
    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    // Se não tem o padrão esperado, retorna como veio
    return phone;
  }

  /**
   * Formata CEP no padrão 00000-000 (EXATAMENTE IGUAL AO ANGULAR)
   */
  private formatCep(cep: string): string {
    if (!cep) return '-';

    // Remove caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '');

    // Verifica se tem 8 dígitos
    if (cleanCep.length !== 8) return cep;

    // Aplica a máscara
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
}
